import { router } from '@inertiajs/react';

import { trackEvent } from '@/lib/analytics';
import { resolveNativePlatform } from '@/lib/capacitor';

const APP_PATHS = ['/app', '/aplikacja'];
const RETRY_DELAYS = [250, 1_000, 2_000];

function csrfToken(documentObject = globalThis.document) {
  return (
    documentObject?.querySelector('meta[name="csrf-token"]')?.content || ''
  );
}

export function safePushActionPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/')) return null;

  let url;
  try {
    url = new URL(value, 'https://amortal.me');
  } catch {
    return null;
  }
  if (url.origin !== 'https://amortal.me') return null;

  const allowed = APP_PATHS.some(
    (prefix) =>
      url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
  );
  return allowed ? `${url.pathname}${url.search}` : null;
}

async function loadPushPlugin() {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  return { plugin: PushNotifications };
}

function wait(milliseconds) {
  return new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));
}

async function captureWithSentry(error) {
  const { default: Sentry } = await import('@/lib/sentry');
  Sentry.captureException(error);
}

async function addSentryBreadcrumb(breadcrumb) {
  const { default: Sentry } = await import('@/lib/sentry');
  Sentry.addBreadcrumb(breadcrumb);
}

export function createPushNotifications({
  nativePlatform = resolveNativePlatform,
  loadPlugin = loadPushPlugin,
  fetchImpl = (...args) => globalThis.fetch(...args),
  navigate = (path) => router.visit(path),
  captureException = captureWithSentry,
  addBreadcrumb = addSentryBreadcrumb,
  track = trackEvent,
  delay = wait,
  documentObject = globalThis.document,
} = {}) {
  let pluginPromise;
  let listenerHandles = [];
  let listenersReady = false;
  let currentToken;
  let registrationUrl;

  const platform = () => nativePlatform();
  const plugin = () => (pluginPromise ||= loadPlugin());

  async function send(method, url, token) {
    const response = await fetchImpl(url, {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(documentObject),
      },
      body: JSON.stringify({ token, platform: platform().platform }),
    });
    return response.ok;
  }

  async function registerWithRetry(token) {
    for (const retryDelay of RETRY_DELAYS) {
      try {
        if (await send('POST', registrationUrl, token)) return true;
      } catch {
        // A native registration can race a connectivity transition.
      }
      await delay(retryDelay);
    }
    return false;
  }

  async function installListeners(pushPlugin) {
    if (listenersReady) return;

    listenerHandles = await Promise.all([
      pushPlugin.addListener('registration', async ({ value }) => {
        currentToken = value;
        if (!(await registerWithRetry(value))) {
          void addBreadcrumb({
            category: 'push',
            level: 'warning',
            message: 'Provider token registration failed after retries',
          });
        }
      }),
      pushPlugin.addListener('registrationError', (error) => {
        captureException(
          new Error(
            `Native push registration failed: ${error?.error || 'unknown'}`,
          ),
        );
      }),
      pushPlugin.addListener('pushNotificationActionPerformed', (action) => {
        void track('push_opened', { platform: platform().platform });
        const path = safePushActionPath(action.notification?.data?.action_path);
        if (path) navigate(path);
      }),
    ]);
    listenersReady = true;
  }

  async function register(pushPlugin) {
    await installListeners(pushPlugin);
    await pushPlugin.register();
  }

  return {
    async initialize({ url }) {
      if (!platform().native || !url) return false;

      registrationUrl = url;
      const { plugin: pushPlugin } = await plugin();
      const permission = await pushPlugin.checkPermissions();
      if (permission.receive !== 'granted') return false;

      await register(pushPlugin);
      return true;
    },

    async requestPermission({ url }) {
      if (!platform().native || !url) return false;

      registrationUrl = url;
      const { plugin: pushPlugin } = await plugin();
      const permission = await pushPlugin.requestPermissions();
      void track('push_permission', {
        granted: permission.receive === 'granted',
        platform: platform().platform,
      });
      if (permission.receive !== 'granted') return false;

      await register(pushPlugin);
      return true;
    },

    async disable({ url }) {
      if (!platform().native || !url) return false;

      const { plugin: pushPlugin } = await plugin();
      if (currentToken) await send('DELETE', url, currentToken);
      await pushPlugin.unregister();
      await Promise.allSettled(
        listenerHandles.map((handle) => handle.remove()),
      );
      listenerHandles = [];
      listenersReady = false;
      currentToken = undefined;
      return true;
    },
  };
}

const pushNotifications = createPushNotifications();

export const initializePushNotifications = (options) =>
  pushNotifications.initialize(options);
export const requestPushPermission = (options) =>
  pushNotifications.requestPermission(options);
export const disablePushNotifications = (options) =>
  pushNotifications.disable(options);
